<template>
  <div :class="props.type" class="product-content">
    <div class="product-container">
      <div class="product-main">
        <ProductImages :images="data.images" :type="props.type" />

        <div
          :class="{ smallContent: smallContent }"
          class="product-description-container"
        >
          <div
            ref="productDescriptionWrapper"
            class="product-description-wrapper"
          >
            <p v-if="type === 'sample'" class="product-type">
              {{ formattedCollectionName }}
            </p>
            <p v-else class="product-type">{{ data.productType }}</p>
            <h2 class="product-title">
              {{ data.displayTitle ? data.displayTitle.value : data.title }}
            </h2>
            <p class="product-description" v-html="data.descriptionHtml" />

            <NuxtLink
              v-if="props.type === 'sample'"
              class="wtb"
              to="/get-started"
            >
              Where to buy
            </NuxtLink>

            <div v-if="variantOptions.length > 0" class="variant-selection">
              <div
                v-for="option in variantOptions"
                :key="option.name"
                class="variant-option"
              >
                <label :for="option.name">{{ option.name }}</label>
                <select
                  :id="option.name"
                  v-model="selectedOptions[option.name]"
                  @change="updateSelectedVariant"
                >
                  <option
                    v-for="value in option.values"
                    :key="value"
                    :value="value"
                  >
                    {{ value }}
                  </option>
                </select>
              </div>
            </div>

            <!-- temporary hide price for care and maintenance products -->
            <p
              v-if="
                currentVariant?.price.amount > 0 &&
                !['Care', 'Maintenance'].includes(data.productType)
              "
              class="product-price"
            >
              {{ formatPrice(currentVariant.price) }}
            </p>

            <div v-if="isAnyButtonVisible" class="buttons-container">
              <!-- TODO GABRIEL : ADD BUY LOGIC -->
              <RoundButton
                v-if="
                  props.type === 'product' &&
                  !['Care', 'Maintenance'].includes(data.productType)
                "
                class="product-button product-buy"
                :dark="true"
                label="Order Sample"
                @click="addToCart"
              />
              <RoundButton
                v-else-if="props.type === 'sample' && !data.notForSample?.value"
                class="product-button product-buy"
                :dark="true"
                label="Order Sample"
                @click="addToCart"
              />

              <RoundButton
                v-if="props.type === 'sample'"
                class="product-button product-wishlist"
                :dark="false"
                :transparent="true"
                :label="
                  items?.includes(data.handle)
                    ? 'Remove from project'
                    : 'Add to project'
                "
                @click="
                  items?.includes(data.handle)
                    ? wishlist.removeItem(data.handle)
                    : wishlist.addItem(data.handle)
                "
              />
            </div>

            <div v-if="validSpecs.length > 0" class="specifications-list">
              <div
                v-for="(spec, index) in validSpecs"
                :key="spec.title"
                class="specification-item"
                @click="toggleSpec(index)"
              >
                <div class="spec-title-container">
                  <h2 class="spec-title">{{ spec.title }}</h2>
                  <span class="cross">
                    <svg
                      ref="crossIcon"
                      class="cross-icon"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      :class="{ 'is-open': isOpen[index] }"
                    >
                      <line
                        x1="4"
                        y1="12"
                        x2="20"
                        y2="12"
                        stroke="currentColor"
                        stroke-width="1"
                        :style="{
                          transformOrigin: 'center',
                          transform: isOpen[index] ? 'scaleY(0)' : 'scaleY(1)',
                          transition: 'transform 0.3s $ease-in-out-quad',
                        }"
                      />
                      <line
                        x1="12"
                        y1="4"
                        x2="12"
                        y2="20"
                        stroke="currentColor"
                        stroke-width="1"
                      />
                    </svg>
                  </span>
                </div>

                <div ref="specText" class="spec-text-container">
                  <p class="spec-text" v-html="spec.value" />
                </div>
              </div>
            </div>

            <div v-if="validDetails.length > 0" class="product-details">
              <h2 class="product-details-label">Product Specifications</h2>
              <div class="product-details-grid">
                <div
                  v-for="item in validDetails"
                  :key="item.title"
                  class="product-detail-item"
                >
                  <h3 class="product-detail-title">{{ item.title }}</h3>
                  <p class="product-detail-text" v-html="item.value" />
                </div>
              </div>
            </div>
            <div v-if="validDetails.length > 0" class="buttons-details-wrapper">
              <div class="buttons-container buttons-details">
                <!-- <RoundButton
                  v-if="props.type === 'product'"
                  class="product-button product-buy"
                  :dark="true"
                  label="Buy this Product"
                />
                <RoundButton
                  v-else-if="props.type === 'sample'"
                  class="product-button product-buy"
                  :dark="true"
                  label="Order Sample"
                />

                <RoundButton
                  v-if="props.type === 'sample'"
                  class="product-button product-wishlist"
                  :dark="false"
                  :transparent="true"
                  label="Where to buy"
                /> -->
              </div>
            </div>

            <!-- <VimeoEmbed v-if="props.data.vimeoId1" :id="props.data.vimeoId1" /> -->

            <ProductDocumentation
              v-if="props.data.documents && props.data.documents.length > 0"
              :documents="props.data.documents"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useMenuStore } from "@/stores/menuStore";
import { useCustomerStore } from "@/stores/shopify/customers";
import { useWishlistStore } from "@/stores/wishlist";
import { formatPrice } from "@/utils/shopify";
import { gsap } from "gsap";
import { storeToRefs } from "pinia";
import { debounce } from "throttle-debounce";

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: false,
  },
});

const isAnyButtonVisible = computed(() => {
  return (
    (props.type === "product" &&
      !["Care", "Maintenance"].includes(props.data.productType)) ||
    props.type === "sample"
  );
});

const productDescriptionWrapper = useTemplateRef("productDescriptionWrapper");

const wishlist = useWishlistStore();
const customer = useCustomerStore();
const menuStore = useMenuStore();
const { items } = storeToRefs(wishlist);

const specText = ref([]);
const selectedOptions = ref({});
const currentVariant = ref(null);

const smallContent = ref(false);

const formattedCollectionName = computed(() => {
  if (
    props.type === "sample" &&
    props.data?.collections?.edges?.[0]?.node?.handle
  ) {
    const { formattedText } = useTextFormatter(
      props.data.collections.edges[0].node.handle
    );
    return formattedText;
  }
  return "";
});

const validSpecs = computed(() => {
  return props.data.specificationsList.filter(
    (spec) => spec.value !== null && spec.value !== undefined
  );
});

const validDetails = computed(() => {
  return props.data.specificationsGrid.filter(
    (spec) => spec.value !== null && spec.value !== undefined
  );
});

const isOpen = ref(validSpecs.value.map(() => false));

const toggleSpec = (index) => {
  isOpen.value[index] = !isOpen.value[index];

  if (isOpen.value[index]) {
    gsap.to(specText.value[index], {
      height: "auto",
      duration: 0.4,
      ease: "power.in",
    });
  } else {
    gsap.to(specText.value[index], {
      height: 0,
      duration: 0.4,
      ease: "power.out",
    });
  }
};

const variants = computed(() => {
  return (
    props.data.variants?.edges?.map((edge) => ({
      id: edge.node.id,
      title: edge.node.title,
      availableForSale: edge.node.availableForSale,
      selectedOptions: edge.node.selectedOptions,
      price: edge.node.price,
    })) || []
  );
});

const variantOptions = computed(() => {
  return (
    props.data.options
      ?.map((option) => ({
        name: option.name,
        values: option.values,
      }))
      .filter((option) => option.name !== "Title") || []
  );
});

onMounted(() => {
  variantOptions.value.forEach((option) => {
    selectedOptions.value[option.name] = option.values[0];
  });

  const titleOption = props.data.options?.find(
    (option) => option.name === "Title"
  );
  if (titleOption) {
    selectedOptions.value["Title"] = titleOption.values[0];
  }

  const debouncedResize = debounce(300, onResize);

  window.addEventListener("resize", debouncedResize);

  updateSelectedVariant();

  onResize();
});

const onResize = () => {
  if (productDescriptionWrapper.value) {
    const descriptionWrapperHeight =
      productDescriptionWrapper.value.offsetHeight;

    smallContent.value = descriptionWrapperHeight < window.innerHeight;
  }
};

const updateSelectedVariant = () => {
  const matchingVariant = variants.value.find((variant) => {
    return variant.selectedOptions.every(
      (option) => selectedOptions.value[option.name] === option.value
    );
  });

  currentVariant.value = matchingVariant;
};

const addToCart = () => {
  if (!currentVariant.value?.id) {
    console.error("No variant selected");
    return;
  }

  customer.addToCart(currentVariant.value.id, 1);
  menuStore.openCart();
};

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<style lang="scss" scoped>
.product-container {
  padding: 0 0 $padding-mobile-big;
  @include desktop {
    padding-bottom: $padding-desktop-big;
  }
}

.product-main {
  display: flex;
  flex-direction: column;
  padding: 10rem var(--padding-mobile) 0;
  background-color: $beige;

  @include desktop {
    padding: 0;
    flex-direction: row;
    position: relative;
    min-height: 100vh;
  }
}

.product-description-container {
  @include desktop {
    width: 50%;
    background-color: $white;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-column-gap: 2.4rem;
    padding-top: 15rem;
    padding-right: 2.4rem;
    padding-left: 1.2rem;
  }

  &.smallContent {
    @include desktop {
      padding: 0;
      align-items: center;

      .product-description-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }
  }
}

.product-description-wrapper {
  @include desktop {
    grid-column: 2 / span 4;
    height: fit-content;
  }
}

.product-type {
  @include caption-medium;
  margin-bottom: 1.5rem;
}

.product-title {
  @include title-large-a;
  text-transform: uppercase;
  margin-bottom: 3rem;

  @include desktop {
    font-family: $suisse-regular;
    font-size: 5.6rem;
    font-style: normal;
    font-weight: 400;
    line-height: 92%; /* 51.52px */
    letter-spacing: -0.14rem;
    text-transform: uppercase;
    margin-bottom: 4.8rem;
  }
}

.product-description {
  @include body-regular;
  margin-bottom: 2rem;
  :deep(p) {
    margin-bottom: 1rem;
  }
}

.wtb {
  @include link;
  @include link-active-full-hover;
  text-transform: uppercase;
  display: block;
  margin: 2rem 0 5rem;
  position: relative;
  line-height: normal !important;
}

.product-price {
  @include caption-medium;
  margin-bottom: 1rem;
}

.buttons-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 4rem;
  width: 100%;
  max-width: 100%;
}

.specifications-list {
  margin-bottom: $padding-mobile-big;
  @include desktop {
    margin-bottom: 0;
  }
}

.specification-item {
  padding: 2rem 0;
  border-top: solid 0.5px $black;
  overflow: hidden;
  @include desktop {
    cursor: pointer;
  }
}

.spec-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.spec-title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cross {
  font-size: 2rem;
  font-family: $beaufort-regular;
}

.question-text {
  @include body-small;
}

.spec-text-container {
  overflow: hidden;
  height: 0;
}

.spec-title {
  @include body-small;
}

.spec-text {
  @include body-regular;
  position: relative;
  top: 2rem;
  padding-bottom: 2rem;
  @include desktop {
    width: 40rem;
  }
}

.cross-icon {
  transition: 0.3s $ease-in-out-quad;
  &.is-open {
    transform: rotate(90deg);
  }
}

.product-details {
  padding: 0;
  margin-top: 6rem;
  @include grid-responsive;

  @include desktop {
    padding: 0;

    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-column-gap: 2.4rem;
  }
}

.product-details-label {
  grid-row-start: 1;
  grid-column: 1 / span 12;
  @include caption-medium;
  margin-bottom: 2rem;

  @include desktop {
    grid-column: 1 / span 6;
  }
}

.product-details-grid {
  grid-row-start: 2;

  grid-column: 1 / span 12;

  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: 1.2rem;

  @include desktop {
    grid-row-start: 2;

    grid-column: 1 / span 6;

    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 2.4rem;
  }
}

.product-detail-item {
  grid-column: span 6;
  padding-bottom: 5rem;
  position: relative;
  padding-top: 1rem;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: rgba($color: #000000, $alpha: 0.5);
  }

  @include desktop {
    grid-column: span 2;
  }
}

.product-detail-title {
  @include body-regular;
  margin-bottom: 0.5rem;
}

.product-detail-text {
  @include body-regular;
  color: rgba($color: #000000, $alpha: 0.5);
}

.buttons-details-wrapper {
  padding-bottom: $padding-mobile-big;
  @include desktop {
    @include grid-desktop;
    padding-bottom: 6rem;
  }
}

.buttons-details {
  padding: 0;
  @include desktop {
    padding: unset;
    grid-column: 1 / span 4;
  }
}

.product-button {
  padding: 1.8rem 4.2rem;
}

.variant-selection {
  margin-bottom: 4rem;
}

.variant-option {
  margin-bottom: 1.5rem;

  label {
    display: block;
    @include caption-medium;
    margin-bottom: 0.5rem;
  }

  select {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba($black, 0.2);
    border-radius: 0;
    background-color: $white;
    @include body-regular;

    &:focus {
      outline: none;
      border-color: $black;
    }
  }
}
</style>
